import { z } from 'zod';

// 病院情報の型定義
export const HospitalSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type Hospital = z.infer<typeof HospitalSchema>;

// アップローダー（投稿者）情報の型定義
export const UploaderSchema = z.object({
  id: z.union([z.string(), z.number()]), // 必須項目
  name: z.string(),
  icon_path: z.string().optional(),
  hospital: HospitalSchema.optional(),
});
export type Uploader = z.infer<typeof UploaderSchema>;

// 動画画像の型定義
export const VideoImageSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  image_path: z.string(),
  description: z.string().optional(),
  display_order: z.number().nullable().optional(),
  created_at: z.string().optional(),
});
export type VideoImage = z.infer<typeof VideoImageSchema>;

// 診療科情報
export const SpecialtySchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
});
export type Specialty = z.infer<typeof SpecialtySchema>;

// 動画に付けるキー付きタグ（例: 部位:膝）。診療科ごとの専用カラムの代わりにこれで分類する
export const SpecialtyAttributeSchema = z.object({
  attribute_key: z.string(),
  attribute_value: z.string(),
});
export type SpecialtyAttribute = z.infer<typeof SpecialtyAttributeSchema>;

// 診療科ごとの属性キー候補（タグ入力のサジェスト用）
export const SpecialtyAttributeKeySchema = z.object({
  attribute_key: z.string(),
  display_order: z.number(),
});
export type SpecialtyAttributeKey = z.infer<typeof SpecialtyAttributeKeySchema>;

// 一覧の診療科フィルタで「診療科が未設定の動画」を指すときの値。
// バックエンド videoController.types.ts の UNCLASSIFIED_SPECIALTY_KEY と同じ値（通信上の約束）
export const UNCLASSIFIED_SPECIALTY_KEY = 'unclassified';
export const UNCLASSIFIED_SPECIALTY_LABEL = '未分類';
// フィルタ候補の先頭に置く「選択解除」の項目
export const ALL_SPECIALTIES_LABEL = 'すべての診療科';

// 一覧の診療科フィルタ用: いま見える動画が1本以上ある診療科と、その本数
export const SpecialtyCountSchema = z.object({
  id: z.string(),
  name: z.string(),
  count: z.number(),
});
export type SpecialtyCount = z.infer<typeof SpecialtyCountSchema>;

export const SpecialtyCountsResSchema = z.object({
  specialties: z.array(SpecialtyCountSchema),
  unclassified_count: z.number(),
});
export type SpecialtyCountsRes = z.infer<typeof SpecialtyCountsResSchema>;

// 基本動画情報の型定義
export const VideoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string().optional(),
  name: z.string().optional(),
  thumbnail_url: z.string().optional(),
  thumbnail: z.string().optional(),
  path: z.string().optional(),
  view_count: z.number().optional(),
  views: z.number().optional(), // viewsを追加（互換性のため）
  duration: z.number().optional(),
  publish_date: z.string().optional(),
  created_at: z.string().optional(),
  // 手術日。VideoDetailInfo / VideoCardSide が既に表示に使っているのに
  // API 型だけ宣言が漏れていた（未設定だと当日日付にフォールバックし、
  // 「手術より前に投稿された動画」に見える）。
  surgery_date: z.string().optional(),
  published_at: z.string().optional(),
  category_id: z.number().optional(),
  deleted_at: z.string().optional(),
  visibility: z.enum(['public', 'private', 'hospital_only']).optional(),
  tags: z.string().optional(),
  uploader: UploaderSchema.optional(),
  uploader_id: z.union([z.string(), z.number()]).optional(),
  description: z.string().optional(),
  images: z.array(VideoImageSchema).optional(),
  // 「資料 N」バッジ用。一覧APIは id のみ返す（件数が分かれば十分なため）
  materials: z.array(z.object({ id: z.union([z.string(), z.number()]) })).optional(),
  is_liked: z.boolean().optional(),
  is_favorited: z.boolean().optional(),
  specialty: SpecialtySchema.optional(),
  specialty_attributes: z.array(SpecialtyAttributeSchema).optional(),
  // 処理中動画用のフィールド
  is_processing: z.boolean().optional(),
  processing_status: z.enum(['in_progress', 'processing']).optional(),
  session_id: z.string().optional(),
  // VP9変換の状態。サムネイル未設定カードのスピナー表示可否の判定に使う
  conversion_status: z.enum(['pending', 'converting', 'completed', 'failed']).optional(),
});
export type Video = z.infer<typeof VideoSchema>;

// VideoEdit用のビデオ型（一部フィールドが必須）
export interface EditableVideo {
  id: string | number;
  title: string;
  description?: string;
  visibility?: 'public' | 'private' | 'hospital_only';

  tags?: string;
  specialty?: {
    id: string;
    name: string;
  };
  specialty_attributes?: SpecialtyAttribute[];
}

// VideoList用のビデオ型（uploader必須）
export interface ListableVideo {
  id: string | number;
  title?: string;
  name?: string;
  thumbnail_url?: string;
  thumbnail?: string;
  view_count?: number;
  duration?: number;
  publish_date?: string;
  created_at?: string;
  surgery_date?: string;
  published_at?: string;
  visibility?: 'public' | 'private' | 'hospital_only';

  uploader: Uploader;
  uploader_id: string | number;
  description?: string;
  images?: VideoImage[];
}

// ページネーション情報の型定義
export const PaginationSchema = z.object({
  totalItems: z.number(),
  currentPage: z.number(),
  totalPages: z.number(),
  pageSize: z.number().optional(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

// 動画一覧レスポンスの型定義
export const VideoResponseSchema = z.object({
  videos: z.array(VideoSchema),
  pagination: PaginationSchema,
});
export type VideoResponse = z.infer<typeof VideoResponseSchema>;

// 単一動画取得レスポンスの型定義
export const SingleVideoResponseSchema = VideoSchema;
export type SingleVideoResponse = z.infer<typeof SingleVideoResponseSchema>;

// 動画アップロードリクエストの型定義
export interface VideoUploadReq {
  file: File;
  title: string;
  description?: string;
  tags?: string;
  visibility?: 'public' | 'private' | 'hospital_only';

}

// 動画アップロードレスポンスの型定義
export const VideoUploadResSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  video: VideoSchema.optional(),
});
export type VideoUploadRes = z.infer<typeof VideoUploadResSchema>;

// 動画更新リクエストの型定義
export interface VideoUpdateReq {
  title?: string;
  description?: string;
  tags?: string;
  visibility?: 'public' | 'private' | 'hospital_only';

  specialty_id?: number | string;
  // キー付きタグ。渡すとサーバー側で全置換される
  specialty_attributes?: SpecialtyAttribute[];
}

// 動画更新レスポンスの型定義
export const VideoUpdateResSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  video: VideoSchema.optional(),
});
export type VideoUpdateRes = z.infer<typeof VideoUpdateResSchema>;

// 動画トリミングリクエストの型定義
export interface VideoTrimReq {
  videoId: string | number;
  startTime: number;
  endTime: number;
}

// 動画トリミングレスポンスの型定義
export const VideoTrimResSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  video: VideoSchema.optional(),
});
export type VideoTrimRes = z.infer<typeof VideoTrimResSchema>;

// 検索サジェスト型（Header.tsx向け）
export interface SearchSuggestion {
  id?: string | number;
  type: string;
  value: string;
  search_text?: string;
  display_text?: string;
}

// 動画検索オプションの型定義
export interface VideoSearchOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  search_query?: string;
  status?: string;
  hospital_only?: boolean;
  // 既存コードとの互換性のため追加
  sort_by?: string;
  sort_order?: string;
}

// 診療科情報レスポンスの型定義
export const SpecialtiesResSchema = z.object({
  specialties: z.array(z.object({
    id: z.union([z.string(), z.number()]),
    name: z.string(),
  })),
});
export type SpecialtiesRes = z.infer<typeof SpecialtiesResSchema>; 
